const ZIP_LOCAL_HEADER = 0x04034b50;
const ZIP_CENTRAL_HEADER = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIR = 0x06054b50;
const ZIP_END_RECORD_SIZE = 22;
const ZIP_MAX_COMMENT = 0xffff;
const ZIP_CENTRAL_HEADER_SIZE = 46;
const ZIP_LOCAL_HEADER_SIZE = 30;
const ZIP_OVERFLOW = 0xffffffff;
const METHOD_STORED = 0;
const METHOD_DEFLATE = 8;

export interface StoredFile {
  blob: Blob;
  fileName: string;
}

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

/**
 * Devolve o arquivo original de um mapa/log guardado em .zip (uma entrada) ou em gzip. O .ftm já é
 * gzip nativo do FT Manager, então fica como veio. Se não der para decodificar, devolve os bytes crus.
 */
export async function unwrapStoredFile(blob: Blob, fileName: string): Promise<StoredFile> {
  try {
    if (await isZip(blob)) return await unzipFirstEntry(blob, fileName);
    if ((await isGzip(blob)) && !fileName.toLowerCase().endsWith('.ftm')) {
      const gunzipped = await decompress(blob, 'gzip');
      // A Vision já subiu gzip por cima do .zip (Sync_compress).
      return (await isZip(gunzipped)) ? await unzipFirstEntry(gunzipped, fileName) : { blob: gunzipped, fileName };
    }
  } catch (err) {
    console.warn('Não foi possível descompactar o arquivo; salvando como está.', err);
  }
  return { blob, fileName };
}

async function isZip(blob: Blob): Promise<boolean> {
  const magic = await readView(blob, 0, 4);
  return magic.byteLength === 4 && magic.getUint32(0, true) === ZIP_LOCAL_HEADER;
}

async function isGzip(blob: Blob): Promise<boolean> {
  const magic = await readView(blob, 0, 2);
  return magic.byteLength === 2 && magic.getUint8(0) === 0x1f && magic.getUint8(1) === 0x8b;
}

async function unzipFirstEntry(blob: Blob, fileName: string): Promise<StoredFile> {
  const entry = await readFirstEntry(blob);
  const dataStart = await readDataStart(blob, entry);
  const data = blob.slice(dataStart, dataStart + entry.compressedSize);

  let original: Blob;
  if (entry.method === METHOD_STORED) {
    original = data;
  } else if (entry.method === METHOD_DEFLATE) {
    original = await decompress(data, 'deflate-raw');
  } else {
    throw new Error(`Método de compressão ${entry.method} não suportado.`);
  }
  if (original.size !== entry.uncompressedSize) {
    throw new Error('Tamanho descompactado diferente do informado no zip.');
  }
  return { blob: original, fileName: savedName(fileName, entry.name) };
}

async function readFirstEntry(blob: Blob): Promise<ZipEntry> {
  const tailStart = Math.max(0, blob.size - ZIP_END_RECORD_SIZE - ZIP_MAX_COMMENT);
  const tail = await readView(blob, tailStart, blob.size);
  let end = -1;
  for (let i = tail.byteLength - ZIP_END_RECORD_SIZE; i >= 0; i--) {
    if (
      tail.getUint32(i, true) === ZIP_END_OF_CENTRAL_DIR &&
      i + ZIP_END_RECORD_SIZE + tail.getUint16(i + 20, true) === tail.byteLength
    ) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new Error('Fim do diretório central do zip não encontrado.');

  const entries = tail.getUint16(end + 10, true);
  const centralOffset = tail.getUint32(end + 16, true);
  if (entries === 0) throw new Error('Zip sem entradas.');
  if (centralOffset === ZIP_OVERFLOW) throw new Error('Zip64 não suportado.');

  const central = await readView(blob, centralOffset, centralOffset + ZIP_CENTRAL_HEADER_SIZE + ZIP_MAX_COMMENT);
  if (central.byteLength < ZIP_CENTRAL_HEADER_SIZE || central.getUint32(0, true) !== ZIP_CENTRAL_HEADER) {
    throw new Error('Diretório central do zip inválido.');
  }
  const flags = central.getUint16(8, true);
  const compressedSize = central.getUint32(20, true);
  const uncompressedSize = central.getUint32(24, true);
  const nameLength = central.getUint16(28, true);
  const localHeaderOffset = central.getUint32(42, true);
  if ((flags & 1) !== 0) throw new Error('Zip criptografado não suportado.');
  if (compressedSize === ZIP_OVERFLOW || uncompressedSize === ZIP_OVERFLOW || localHeaderOffset === ZIP_OVERFLOW) {
    throw new Error('Zip64 não suportado.');
  }
  if (central.byteLength < ZIP_CENTRAL_HEADER_SIZE + nameLength) throw new Error('Diretório central do zip truncado.');

  const nameBytes = new Uint8Array(central.buffer, central.byteOffset + ZIP_CENTRAL_HEADER_SIZE, nameLength);
  return {
    name: new TextDecoder().decode(nameBytes),
    method: central.getUint16(10, true),
    compressedSize,
    uncompressedSize,
    localHeaderOffset,
  };
}

async function readDataStart(blob: Blob, entry: ZipEntry): Promise<number> {
  const local = await readView(blob, entry.localHeaderOffset, entry.localHeaderOffset + ZIP_LOCAL_HEADER_SIZE);
  if (local.byteLength < ZIP_LOCAL_HEADER_SIZE || local.getUint32(0, true) !== ZIP_LOCAL_HEADER) {
    throw new Error('Cabeçalho local do zip inválido.');
  }
  return entry.localHeaderOffset + ZIP_LOCAL_HEADER_SIZE + local.getUint16(26, true) + local.getUint16(28, true);
}

/** Mantém o nome já salvo quando ele já termina com a extensão original; senão usa o nome da entrada. */
function savedName(fileName: string, entryName: string): string {
  const original = entryName.split(/[\\/]/).pop() ?? '';
  if (!original) return fileName;
  const dot = original.lastIndexOf('.');
  const extension = dot >= 0 ? original.slice(dot).toLowerCase() : '';
  const stored = fileName.toLowerCase();
  return stored.endsWith('.zip') || !stored.endsWith(extension) ? original : fileName;
}

async function readView(blob: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

function decompress(blob: Blob, format: CompressionFormat): Promise<Blob> {
  return new Response(blob.stream().pipeThrough(new DecompressionStream(format))).blob();
}
